import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, Users, AlertTriangle, TrendingUp, Clock, Target,
  ChevronDown, ChevronUp, Loader2, ExternalLink, CheckCircle2,
  XCircle, AlertCircle, Activity
} from 'lucide-react';
import { api } from '../api/client';

// ==========================================
// DEMAND VS CAPACITY BAR CHART
// ==========================================
const DemandCapacityChart = ({ demandData, capacityData, allocatedData, height = 120 }) => {
  const months = Object.keys(demandData || {}).sort().slice(0, 6);
  
  if (months.length === 0) {
    return (
      <div className="flex items-center justify-center h-24 text-gray-400 text-sm">
        No demand data available
      </div>
    );
  }

  const maxValue = Math.max(
    ...months.map(m => Math.max(
      demandData[m] || 0,
      allocatedData?.[m] || 0,
      80 // Minimum for visual
    ))
  );

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2" style={{ height }}>
        {months.map((month) => {
          const demand = demandData[month] || 0;
          const allocated = allocatedData?.[month] || 0;
          const demandHeight = Math.max((demand / maxValue) * 100, 4);
          const allocHeight = Math.max((allocated / maxValue) * 100, 4);
          
          const isUnderAllocated = demand > allocated && allocated > 0;
          
          return (
            <div key={month} className="flex-1 flex flex-col items-center gap-1">
              <div className="flex items-end gap-0.5 w-full h-full">
                {/* Demand Bar */}
                <div
                  className={`flex-1 rounded-t transition-all ${
                    isUnderAllocated ? 'bg-amber-500' : 'bg-purple-500'
                  }`}
                  style={{ height: `${demandHeight}%` }}
                  title={`Demand: ${demand}h`}
                />
                {/* Allocated Bar */}
                <div
                  className="flex-1 bg-green-500 rounded-t transition-all"
                  style={{ height: `${allocHeight}%` }}
                  title={`Allocated: ${allocated}h`}
                />
              </div>
              <span className="text-[10px] text-gray-500">{month.slice(5)}</span>
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-purple-500 rounded" />
          <span className="text-gray-600">Demand</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded" />
          <span className="text-gray-600">Allocated</span>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// DELIVERY ALIGNMENT SUMMARY
// ==========================================
const DeliveryAlignmentSummary = ({ data, totalItems }) => {
  if (!data || totalItems === 0) {
    return (
      <div className="text-xs text-gray-400 italic">No work items</div>
    );
  }

  const acPercent = Math.round((data.acComplete / totalItems) * 100);
  const depPercent = Math.round((data.dependenciesMapped / totalItems) * 100);
  const highRiskCount = data.highRisk || 0;
  const driftCount = data.scopeDrift || 0;

  return (
    <div className="grid grid-cols-2 gap-3">
      {/* AC Complete */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-600">AC Complete</span>
          <CheckCircle2 className={`w-4 h-4 ${acPercent >= 80 ? 'text-green-500' : acPercent >= 50 ? 'text-amber-500' : 'text-gray-400'}`} />
        </div>
        <div className="flex items-end gap-2">
          <span className="text-xl font-bold text-gray-900">{acPercent}%</span>
          <span className="text-xs text-gray-500 mb-1">{data.acComplete}/{totalItems}</span>
        </div>
      </div>

      {/* Dependencies Mapped */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-600">Deps Mapped</span>
          <Activity className={`w-4 h-4 ${depPercent >= 80 ? 'text-green-500' : depPercent >= 50 ? 'text-amber-500' : 'text-gray-400'}`} />
        </div>
        <div className="flex items-end gap-2">
          <span className="text-xl font-bold text-gray-900">{depPercent}%</span>
          <span className="text-xs text-gray-500 mb-1">{data.dependenciesMapped}/{totalItems}</span>
        </div>
      </div>

      {/* High Risk Items */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-600">High Risk</span>
          <AlertTriangle className={`w-4 h-4 ${highRiskCount > 0 ? 'text-red-500' : 'text-green-500'}`} />
        </div>
        <div className="flex items-end gap-2">
          <span className={`text-xl font-bold ${highRiskCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {highRiskCount}
          </span>
          <span className="text-xs text-gray-500 mb-1">items</span>
        </div>
      </div>

      {/* Scope Drift */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-600">Scope Drift</span>
          <Target className={`w-4 h-4 ${driftCount > 0 ? 'text-amber-500' : 'text-green-500'}`} />
        </div>
        <div className="flex items-end gap-2">
          <span className={`text-xl font-bold ${driftCount > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
            {driftCount}
          </span>
          <span className="text-xs text-gray-500 mb-1">items</span>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// CONFLICTS LIST
// ==========================================
const ConflictsList = ({ conflicts }) => {
  const conflictMonths = Object.keys(conflicts || {}).sort();
  
  if (conflictMonths.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
        <CheckCircle2 className="w-4 h-4 text-green-600" />
        <span className="text-sm text-green-700">No resource conflicts detected</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {conflictMonths.slice(0, 3).map(month => {
        const conflict = conflicts[month];
        return (
          <div 
            key={month}
            className="p-3 bg-amber-50 rounded-lg border border-amber-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-900">{month}</span>
              </div>
              <span className="text-xs text-amber-700">
                Gap: {conflict.gap}h
              </span>
            </div>
            <div className="flex items-center gap-4 mt-1 text-xs text-amber-700">
              <span>Demand: {conflict.demand}h</span>
              <span>Allocated: {conflict.allocated}h</span>
            </div>
          </div>
        );
      })}
      {conflictMonths.length > 3 && (
        <span className="text-xs text-gray-500">+{conflictMonths.length - 3} more months with conflicts</span>
      )}
    </div>
  );
};

// ==========================================
// MAIN PROJECT RESOURCE ROLLUP SECTION
// ==========================================
export const ProjectResourceRollup = ({
  projectId,
  tenantId,
  onNavigate,
  className = ''
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.getProjectResourceRollup(projectId, tenantId);
      setData(response.data);
    } catch (err) {
      console.error('Failed to load project resource rollup:', err);
      setError(err.response?.data?.detail || 'Failed to load resource data');
    } finally {
      setLoading(false);
    }
  }, [projectId, tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-6 shadow-sm ${className}`}>
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
          <span className="text-gray-600">Loading resource data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-6 shadow-sm ${className}`}>
        <div className="flex items-center gap-3 text-red-600">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { demandSummary, allocationSummary, conflicts, deliveryAlignment, workItemCount } = data;

  // Combine role and team demand for chart
  const combinedDemand = {};
  Object.entries(demandSummary.byRoleMonthly || {}).forEach(([m, v]) => {
    combinedDemand[m] = (combinedDemand[m] || 0) + v;
  });
  Object.entries(demandSummary.byTeamMonthly || {}).forEach(([m, v]) => {
    combinedDemand[m] = (combinedDemand[m] || 0) + v;
  });

  const hasConflicts = Object.keys(conflicts || {}).length > 0;

  return (
    <div 
      className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${className}`}
      data-testid="project-resource-rollup"
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-purple-100 rounded-xl">
            <BarChart3 className="w-6 h-6 text-purple-600" />
          </div>
          <div className="text-left">
            <h2 className="text-lg font-semibold text-gray-900">Resource Demand vs Capacity</h2>
            <p className="text-sm text-gray-500">
              {workItemCount} work items • Total demand: {demandSummary.totalRoleDemand + demandSummary.totalTeamDemand}h
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasConflicts && (
            <div className="flex items-center gap-1 px-2.5 py-1 bg-amber-100 rounded-full">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-xs font-medium text-amber-700">
                {Object.keys(conflicts).length} conflicts
              </span>
            </div>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 space-y-6">
          {/* Demand vs Allocation Chart */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Monthly Demand vs Allocation</h3>
            <DemandCapacityChart
              demandData={combinedDemand}
              allocatedData={allocationSummary.byMonth}
            />
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-purple-600" />
                <span className="text-xs text-purple-700">Total Demand</span>
              </div>
              <span className="text-2xl font-bold text-purple-900">
                {demandSummary.totalRoleDemand + demandSummary.totalTeamDemand}h
              </span>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-xs text-green-700">Allocated</span>
              </div>
              <span className="text-2xl font-bold text-green-900">
                {allocationSummary.totalAllocated}h
              </span>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-blue-700">Work Items</span>
              </div>
              <span className="text-2xl font-bold text-blue-900">
                {workItemCount}
              </span>
            </div>
            
            <div className={`p-4 rounded-lg ${hasConflicts ? 'bg-amber-50' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className={`w-4 h-4 ${hasConflicts ? 'text-amber-600' : 'text-gray-500'}`} />
                <span className={`text-xs ${hasConflicts ? 'text-amber-700' : 'text-gray-600'}`}>Conflicts</span>
              </div>
              <span className={`text-2xl font-bold ${hasConflicts ? 'text-amber-900' : 'text-gray-900'}`}>
                {Object.keys(conflicts || {}).length}
              </span>
            </div>
          </div>

          {/* Delivery Alignment Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Delivery Alignment Summary</h3>
            <DeliveryAlignmentSummary
              data={deliveryAlignment}
              totalItems={workItemCount}
            />
          </div>

          {/* Conflicts Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Resource Conflicts</h3>
            <ConflictsList conflicts={conflicts} />
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={() => onNavigate && onNavigate('/resource/utilisation')}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
              data-testid="view-utilisation-btn"
            >
              <ExternalLink className="w-4 h-4" />
              View Utilisation Heatmap
            </button>
            <button
              onClick={() => onNavigate && onNavigate('/resource/capacity')}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              data-testid="manage-capacity-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Manage Capacity
            </button>
            <button
              onClick={() => onNavigate && onNavigate(`/project/${projectId}/work-items`)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="view-work-items-btn"
            >
              <ExternalLink className="w-4 h-4" />
              View Work Items
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectResourceRollup;
